/**
 * ПРИБОР «ГЛАЗАМИ РЕКТОРА» — учёт открытий вкладок админки (программа «Пульт», строка 1.1, 2 окт 2026).
 *
 * Открытие вкладки — от нажатия (смена адреса страницы; первое открытие загрузки — от начала загрузки) до метки
 * «данные видны», которую ставит сам экран (`useDataVisible`, ui/panel-view.tsx). Экран может поставить
 * несколько меток — открытие готово, когда готовы все; любая сказала «не пришло» — открытие кончилось этим.
 * Ушёл раньше — «ушёл»; за потолок — «завис». Время раскладывается по замерам самого браузера (Resource
 * Timing): пока что-то из сети было в пути, сколько из этого думал наш шлюз (заголовок Server-Timing), остальное
 * — браузер. Маяк — одна строка на открытие в шлюз панели (`POST /v1/panel-view`, только живая сессия админа);
 * не дошёл — ждёт в очереди браузера и уходит со следующим (так видно и «данные не пришли» при упавшей сети).
 * Договор маяка и вкладки — @padla/core/panel/panel-view (одна правда со шлюзом). Смотреть: `padla look panel`.
 */
import {
  PANEL_VIEW_MAX_MS,
  type PanelViewBeacon,
  type PanelViewEnd,
  type PanelViewName,
  type PanelViewRequest,
  type PanelViewUnreadWhy,
  panelApiPathOf,
  panelDeviceOf,
  panelHostOf,
  panelViewOf,
} from '@padla/core/panel/panel-view';

/** Нужное прибору из записи Resource Timing (PerformanceResourceTiming и навигация страницы). */
export interface TimingEntry {
  name: string;
  initiatorType: string;
  startTime: number;
  requestStart: number;
  responseStart: number;
  responseEnd: number;
  transferSize: number;
  decodedBodySize: number;
  responseStatus?: number;
  serverTiming?: ReadonlyArray<{ name: string; duration: number }>;
}

/** Раскладка времени открытия по замерам браузера. */
export interface TimingSplit {
  waitMs: number;
  serverMs: number | null;
  staticMs: number;
  bytes: number;
  reqs: number;
  cached: number;
  slowest: PanelViewRequest | null;
  bad: Array<{ path: string; status: number }>;
  unread: Array<{ path: string; why: PanelViewUnreadWhy }>;
}

type Interval = [number, number];

/** Длина объединения отрезков. */
function unionLength(intervals: Interval[]): number {
  const sorted = intervals.filter(([a, b]) => b > a).sort((x, y) => x[0] - y[0]);
  let total = 0;
  let cur: Interval | null = null;
  for (const [a, b] of sorted) {
    if (!cur || a > cur[1]) {
      if (cur) total += cur[1] - cur[0];
      cur = [a, b];
    } else if (b > cur[1]) cur[1] = b;
  }
  if (cur) total += cur[1] - cur[0];
  return total;
}

const pathnameOf = (url: string): string => {
  try {
    return new URL(url, 'https://panel.padla.pro').pathname;
  } catch {
    return '';
  }
};

/** Путь маяка самого прибора: его отправка — не данные открытия. */
const OWN_BEACON = '/api/v1/panel-view';

/**
 * Что значит запись для открытия: `data` — запрос данных экрана (fetch или XHR на `/api/…`, страница или соседний
 * мост); `static` — чанки, стили, шрифты, картинки, сама страница; `skip` — к ожиданию данных не относится: поток
 * событий (`EventSource`, живой канал — открыт всё время, «ответа шлюза» у него нет; его запись появляется, когда
 * канал закрылся) и маяки (`sendBeacon`, свой маяк прибора).
 */
export function entryKind(e: TimingEntry): 'data' | 'static' | 'skip' {
  const path = pathnameOf(e.name);
  if (!path.startsWith('/api/')) return 'static';
  if (path === OWN_BEACON) return 'skip';
  return e.initiatorType === 'fetch' || e.initiatorType === 'xmlhttprequest' ? 'data' : 'skip';
}

/** Пришёл из кэша браузера — до сервера не ходил. */
const fromCache = (e: TimingEntry): boolean => e.transferSize === 0 && e.decodedBodySize > 0;

const serverDur = (e: TimingEntry): number | null =>
  e.serverTiming?.find((s) => s.name === 'app')?.duration ?? null;

/**
 * Раскладка открытия [start, end]: берутся запросы, начатые в открытии и пришедшие до метки (опрос, начатый
 * раньше, к данным этого открытия не относится). Шлюз неизвестен хоть у одного запроса данных, ходившего в сеть, —
 * `serverMs: null` («не прочитано»), а не заниженное число.
 */
export function splitTiming(
  entries: readonly TimingEntry[],
  start: number,
  end: number
): TimingSplit {
  const inView = entries.filter(
    (e) =>
      e.startTime >= start && e.responseEnd > 0 && e.responseEnd <= end && entryKind(e) !== 'skip'
  );
  const api = inView.filter((e) => entryKind(e) === 'data');
  const busy: Interval[] = inView.map((e) => [e.startTime, e.responseEnd]);
  const staticBusy: Interval[] = inView
    .filter((e) => entryKind(e) === 'static')
    .map((e) => [e.startTime, e.responseEnd]);
  const server: Interval[] = [];
  const unread: Array<{ path: string; why: PanelViewUnreadWhy }> = [];
  for (const e of api) {
    if (fromCache(e)) continue;
    const dur = serverDur(e);
    if (e.responseStart <= 0 || dur === null) {
      unread.push({ path: panelApiPathOf(e.name), why: e.responseStart <= 0 ? 'start' : 'header' });
      continue;
    }
    const from = Math.max(e.requestStart > 0 ? e.requestStart : e.startTime, e.responseStart - dur);
    server.push([from, e.responseStart]);
  }
  let slowest: PanelViewRequest | null = null;
  for (const e of api) {
    const ms = e.responseEnd - e.startTime;
    if (!slowest || ms > slowest.ms)
      slowest = {
        path: panelApiPathOf(e.name),
        ms: Math.round(ms),
        serverMs: serverDur(e) === null ? null : Math.round(serverDur(e) ?? 0),
        bytes: e.transferSize,
        status: e.responseStatus && e.responseStatus > 0 ? e.responseStatus : null,
      };
  }
  return {
    waitMs: Math.round(unionLength(busy)),
    serverMs: unread.length === 0 ? Math.round(unionLength(server)) : null,
    staticMs: Math.round(unionLength(staticBusy)),
    bytes: inView.reduce((s, e) => s + (e.transferSize || 0), 0),
    reqs: api.length,
    cached: api.filter(fromCache).length,
    slowest,
    bad: api
      .filter((e) => (e.responseStatus ?? 0) >= 400)
      .slice(0, 5)
      .map((e) => ({ path: panelApiPathOf(e.name), status: e.responseStatus as number })),
    unread: unread.slice(0, 5),
  };
}

/** Окружение открытия, которое знает страница (устройство, адрес, мост, сеть). */
export interface ViewSurroundings {
  device: PanelViewBeacon['device'];
  tg: boolean;
  host: PanelViewBeacon['host'];
  bridge: PanelViewBeacon['bridge'];
  edge: PanelViewBeacon['edge'];
  rtt: number | null;
  ect: string | null;
}

export interface PanelViewTrackerDeps {
  now: () => number;
  /** Начало загрузки страницы в мс эпохи (performance.timeOrigin). */
  timeOrigin: number;
  /** Записи Resource Timing, собранные к этому моменту (вместе с навигацией страницы). */
  entries: () => readonly TimingEntry[];
  surroundings: () => ViewSurroundings;
  hidden: () => boolean;
  /** Вызвать после следующей отрисовки (спрятанная вкладка не рисует — сразу). */
  afterPaint: (cb: () => void) => void;
  later: (cb: () => void, ms: number) => unknown;
  cancel: (handle: unknown) => void;
  /** Отправить маяк; `unload` — страница уходит (только sendBeacon). Обещание — дошёл ли. */
  send: (body: string, unload: boolean) => Promise<boolean>;
  outbox: { load: () => string[]; save: (items: string[]) => void };
}

type HookState = 'pending' | 'ready' | 'failed';

interface ViewRun {
  id: number;
  name: PanelViewName;
  kind: 'entry' | 'switch';
  start: number;
  hooks: Map<string, HookState>;
  hidden: boolean;
  login: boolean;
  done: boolean;
  painting: boolean;
  stuck: unknown;
}

/** Сколько маяков ждут в очереди браузера самое большее. */
const OUTBOX_MAX = 30;

export interface PanelViewTracker {
  /** Смена адреса страницы: новое открытие (идемпотентно по ключу навигации). Номер открытия или null — не экран. */
  begin(key: string, pathname: string): number | null;
  register(viewId: number, hookId: string): void;
  unregister(viewId: number, hookId: string): void;
  report(viewId: number, hookId: string, state: 'ready' | 'failed'): void;
  /** На входе показан экран ключа: время первого открытия включает вход человека. */
  noteLogin(): void;
  /** Вкладку браузера спрятали: время текущего открытия — не о скорости. */
  noteHidden(): void;
  /** Страница уходит: незаконченное открытие — «ушёл», маяком при уходе. */
  leave(): void;
}

export function createPanelViewTracker(deps: PanelViewTrackerDeps): PanelViewTracker {
  let seq = 0;
  let current: ViewRun | null = null;
  let lastKey: string | null = null;
  let lastPath: string | null = null;
  let firstSeen = false;
  let loginShown = false;
  let flushing = false;

  function enqueue(body: string): void {
    try {
      deps.outbox.save([...deps.outbox.load(), body].slice(-OUTBOX_MAX));
    } catch {
      /* очередь — удобство, не обязанность */
    }
  }

  async function flush(): Promise<void> {
    if (flushing) return;
    flushing = true;
    try {
      let items = deps.outbox.load();
      while (items.length > 0) {
        if (!(await deps.send(items[0] as string, false))) break;
        items = items.slice(1);
        deps.outbox.save(items);
      }
    } catch {
      /* следующий маяк попробует снова */
    } finally {
      flushing = false;
    }
  }

  function ship(beacon: PanelViewBeacon, unload: boolean): void {
    const body = JSON.stringify(beacon);
    if (unload) {
      void deps.send(body, true).catch(() => false);
      return;
    }
    void deps
      .send(body, false)
      .then((ok) => {
        if (ok) void flush();
        else enqueue(body);
      })
      .catch(() => enqueue(body));
  }

  function finish(v: ViewRun, end: PanelViewEnd, at: number, unload = false): void {
    if (v.done) return;
    v.done = true;
    deps.cancel(v.stuck);
    const t = Math.max(at, v.start);
    const split = splitTiming(deps.entries(), v.start, t);
    ship(
      {
        view: v.name,
        kind: v.kind,
        end,
        ms: Math.min(Math.round(t - v.start), PANEL_VIEW_MAX_MS),
        ...split,
        marks: v.hooks.size,
        hidden: v.hidden,
        login: v.login,
        ...deps.surroundings(),
        at: Math.round(deps.timeOrigin + v.start),
      },
      unload
    );
  }

  function evaluate(v: ViewRun): void {
    if (v.done || v.painting || v.hooks.size === 0) return;
    const states = [...v.hooks.values()];
    if (states.includes('failed')) {
      finish(v, 'failed', deps.now());
      return;
    }
    if (states.every((s) => s === 'ready')) {
      v.painting = true;
      deps.afterPaint(() => finish(v, 'ready', deps.now()));
    }
  }

  const byId = (id: number): ViewRun | null => (current && current.id === id ? current : null);

  return {
    begin(key, pathname) {
      if (key === lastKey) return current?.id ?? null;
      lastKey = key;
      if (pathname === lastPath) return current?.id ?? null;
      lastPath = pathname;
      const prev = current;
      if (prev && !prev.done) finish(prev, prev.painting ? 'ready' : 'left', deps.now());
      const name = panelViewOf(pathname);
      if (!name) {
        current = null;
        return null;
      }
      const entry = !firstSeen;
      firstSeen = true;
      const start = entry ? 0 : deps.now();
      const v: ViewRun = {
        id: ++seq,
        name,
        kind: entry ? 'entry' : 'switch',
        start,
        hooks: new Map(),
        hidden: deps.hidden(),
        login: entry && loginShown,
        done: false,
        painting: false,
        stuck: null,
      };
      v.stuck = deps.later(
        () => finish(v, 'stuck', deps.now()),
        Math.max(0, start + PANEL_VIEW_MAX_MS - deps.now())
      );
      current = v;
      return v.id;
    },
    register(viewId, hookId) {
      const v = byId(viewId);
      if (!v || v.done || v.hooks.has(hookId)) return;
      v.hooks.set(hookId, 'pending');
    },
    unregister(viewId, hookId) {
      const v = byId(viewId);
      if (!v || v.done) return;
      if (v.hooks.get(hookId) === 'pending') {
        v.hooks.delete(hookId);
        queueMicrotask(() => evaluate(v));
      }
    },
    report(viewId, hookId, state) {
      const v = byId(viewId);
      if (!v || v.done || !v.hooks.has(hookId) || v.hooks.get(hookId) === state) return;
      v.hooks.set(hookId, state);
      // Все метки одной отрисовки успевают встать до решения: проверка — после текущей фиксации React.
      queueMicrotask(() => evaluate(v));
    },
    noteLogin() {
      loginShown = true;
      if (current && current.kind === 'entry') current.login = true;
    },
    noteHidden() {
      if (current && !current.done) current.hidden = true;
    },
    leave() {
      if (current && !current.done)
        finish(current, current.painting ? 'ready' : 'left', deps.now(), true);
    },
  };
}

// ── Браузер ────────────────────────────────────────────────────────────────────────────────────

const OUTBOX_KEY = 'padla-panel-view-outbox';
const ENTRY_CAP = 3000;

let tracker: PanelViewTracker | null = null;

/**
 * Прибор страницы — один на загрузку (вход считается от её начала, сколько бы раз админка ни монтировалась).
 * Окружение — функцией: мост и гонка мостов узнаются по ходу загрузки.
 */
export function panelViewTracker(
  apiBase: string,
  surroundings: () => ViewSurroundings
): PanelViewTracker {
  if (tracker) return tracker;
  const entries: TimingEntry[] = [];
  try {
    const nav = performance.getEntriesByType('navigation')[0] as unknown as TimingEntry | undefined;
    if (nav) entries.push(nav);
    const po = new PerformanceObserver((list) => {
      for (const e of list.getEntries()) entries.push(e as unknown as TimingEntry);
      if (entries.length > ENTRY_CAP) entries.splice(0, entries.length - ENTRY_CAP);
    });
    po.observe({ type: 'resource', buffered: true });
  } catch {
    /* без замеров браузера маяк уйдёт с одним временем */
  }
  const url = `${apiBase}/v1/panel-view`;
  tracker = createPanelViewTracker({
    now: () => performance.now(),
    timeOrigin: performance.timeOrigin,
    entries: () => entries,
    surroundings,
    hidden: () => document.visibilityState === 'hidden',
    afterPaint: (cb) => {
      if (document.visibilityState === 'hidden') cb();
      else requestAnimationFrame(() => setTimeout(cb, 0));
    },
    later: (cb, ms) => setTimeout(cb, ms),
    cancel: (h) => clearTimeout(h as ReturnType<typeof setTimeout>),
    send: async (body, unload) => {
      if (unload)
        return navigator.sendBeacon(url, new Blob([body], { type: 'text/plain;charset=UTF-8' }));
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body,
        keepalive: true,
        credentials: 'include',
      });
      return res.ok;
    },
    outbox: {
      load: () => {
        try {
          const raw = localStorage.getItem(OUTBOX_KEY);
          const list: unknown = raw ? JSON.parse(raw) : [];
          return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string') : [];
        } catch {
          return [];
        }
      },
      save: (items) => {
        try {
          if (items.length === 0) localStorage.removeItem(OUTBOX_KEY);
          else localStorage.setItem(OUTBOX_KEY, JSON.stringify(items));
        } catch {
          /* хранилище закрыто — очередь в этой загрузке не живёт */
        }
      },
    },
  });
  const t = tracker;
  if (loginShownEarly) t.noteLogin();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') t.noteHidden();
  });
  window.addEventListener('pagehide', () => t.leave());
  return t;
}

/**
 * Экран ключа на входе: время первого открытия включает вход человека (метка login). Экран ключа показывается до
 * админки — прибора ещё нет, поэтому отметка живёт здесь и передаётся ему при создании.
 */
let loginShownEarly = false;
export function notePanelLogin(): void {
  loginShownEarly = true;
  tracker?.noteLogin();
}

/** Устройство и сеть страницы — для маяка. */
export function browserSurroundings(edge: {
  bridge: PanelViewBeacon['bridge'];
  own: boolean;
}): ViewSurroundings {
  const nav = navigator as Navigator & {
    userAgentData?: { platform?: string };
    connection?: { rtt?: number; effectiveType?: string };
  };
  const w = window as unknown as { Telegram?: { WebApp?: { initData?: unknown } } };
  const data = w.Telegram?.WebApp?.initData;
  return {
    device: panelDeviceOf(nav.userAgentData?.platform, nav.userAgent),
    tg: typeof data === 'string' && data.length > 0,
    host: panelHostOf(window.location.hostname),
    bridge: edge.bridge,
    edge: edge.own ? 'own' : 'other',
    rtt: typeof nav.connection?.rtt === 'number' ? nav.connection.rtt : null,
    ect: nav.connection?.effectiveType ?? null,
  };
}
